import { UploadWorker } from '../../workers/UploadWorker.js';
import { uuidv4 } from '../../modules/browserUUIDv4.js';
import { GetPhotoSummary } from '../../modules/GetPhotoSummary.js';
import { GetExif } from '../../modules/GetExif.js';

export class Uploader {
  constructor(firebaseConfig, options) {
    options = options || {};
    this._promises = {};
    // determine if we are online or offline based on our connection to firebase
    firebase
      .database()
      .ref('.info/connected')
      .on('value', (s) => {
        const online = s.val();
        if (this.worker) this.worker.postMessage({ call: 'onlineChanged', args: [online] });
        else this.uploadScript?.onlineChanged(online);
      });
    if (options.useWorker) {
      // Create a worker from the clock script
      this.worker = new Worker(`/source/_resources/workers/UploadWorker.js`, { type: 'module' });
      // Post a message to the worker to initialize it
      this.worker.postMessage({ call: 'init', args: [firebaseConfig, window.navigator.onLine, { ...options, isMobile: self.isMobile }] });
      this.worker.addEventListener('message', (e) => {
        if (e.data.type == 'progress') {
          this.lastPromiseKey = e.data.promiseKey;
          this._promises[e.data.promiseKey].data.onProgress(e.data.message);
        } else if (e.data.type == 'complete') {
          this._promises[e.data.promiseKey || this.lastPromiseKey].resolve(e.data.message);
        } else if (e.data.type == 'promiseResponse') {
          this._promises[e.data.promiseKey].resolve(e.data.res);
        }
      });
    } else {
      // Create an instance of the worker to run as a script
      this.uploadScript = new UploadWorker();
      // Initialize the local script with the canvas
      this.uploadScript.init(
        firebaseConfig,
        window.navigator.onLine,
        { ...options, isMobile: self.isMobile },
        (promiseKey, message) => {
          this.lastPromiseKey = promiseKey;
          this._promises[promiseKey].data.onProgress(message);
        },
        (promiseKey, message) => {
          this._promises[promiseKey || this.lastPromiseKey].resolve(message);
        }
      );
    }
  }
  uploadFromFiles(files, options, onProgress) {
    let key = uuidv4();
    let promise = this._promise(key, { onProgress });
    let args = [files, options, key];
    if (this.worker) this.worker.postMessage({ call: 'uploadFromFiles', args });
    else if (this.uploadScript) this.uploadScript.uploadFromFiles(...args);
    return promise;
  }
  uploadFromFilesArray(filesArray, options, onProgress) {
    let key = uuidv4();
    let promise = this._promise(key, { onProgress });
    let args = [filesArray, options, key];
    if (this.worker) this.worker.postMessage({ call: 'uploadFromFilesArray', args });
    else if (this.uploadScript) this.uploadScript.uploadFromFiles(...args);
    return promise;
  }
  upload(photoRecords, onProgress) {
    if (photoRecords.length == 0) return Promise.resolve();
    let key = uuidv4();
    let promise = this._promise(key, { onProgress });
    photoRecords.forEach((x) => (x.promiseKey = key));
    let args = [photoRecords];
    if (this.worker) this.worker.postMessage({ call: 'upload', args });
    else if (this.uploadScript) this.uploadScript.upload(...args);
    return promise;
  }
  checkForExistingPhoto(jobId, photoNameDate) {
    if (this.worker) {
      let key = uuidv4();
      let promise = this._promise(key);
      this.worker.postMessage({ call: 'checkForExistingPhoto', promise: key, args: arguments });
      return promise;
    } else if (this.uploadScript) return this.uploadScript.checkForExistingPhoto(...arguments);
  }
  async uploadJPGs(files, options) {
    if (!options.jobId) throw new Error('Missing JobId');
    if (!options.uid) throw new Error('Missing uid');
    let update = {};
    options.cameraId = options.cameraId || FirebaseWorker.ref().push().key;
    options.folderId = options.folderId || FirebaseWorker.ref().push().key;
    let uploads = [];
    for (let file of files) {
      let exif = await GetExif(file);
      let status = await this.checkForExistingPhoto(options.jobId, exif.name_date);
      file.key = status.existingKey || uuidv4();
      // TODO Handle Cameras and Folders
      if (!status.uploadComplete) {
        let photo = {
          folderId: options.folderId,
          cameraId: options.cameraId,
          status: 'upload_started',
          uploaded_by: options.uid,
          '.priority': '' + options.cameraId + exif.date_taken,
          ...exif
        };
        update['photos/' + file.key] = photo;
        update['photo_summary/' + file.key] = GetPhotoSummary(photo);
        uploads.push(
          firebase
            .storage()
            .ref('photos/' + file.key + '_full.jpg')
            .put(file)
        );
      }
    }
    await FirebaseWorker.ref(`photoheight/jobs/` + options.jobId).update(update);
    await Promise.all(uploads);
    let updateComplete = {};
    for (let file of files) {
      updateComplete['photos/' + file.key + '/status'] = 'upload_complete';
      updateComplete['photo_summary/' + file.key + '/status'] = 'upload_complete';
    }
    await FirebaseWorker.ref(`photoheight/jobs/` + options.jobId).update(updateComplete);
  }
  _promise(key, data) {
    let promise = new Promise((resolve, reject) => (this._promises[key] = { resolve, reject, data }));
    promise.finally(() => delete this._promises[key]);
    return promise;
  }
}
